/**
 * Files view controller — wires the pane's events to the action, lifecycle, and
 * upload modules, and owns sign-out. The heavy lifting lives elsewhere:
 *
 *   files_state.js        one store (queue, files, sending, sentRecipients, lastAttached)
 *   files_view.js         DOM rendering + send-button state + status line
 *   secure_link.js        the card: build + find its HTML, and insert/refresh/
 *                         discard it in the email body (Office.js)
 *   files_actions.js      orchestration: send/re-attach/add/remove/cancel, the
 *                         chunked upload loop, the on-open lifecycle (cleanup →
 *                         resume), and the RecipientsChanged watcher
 *   js/utils/api.js       the /outlook/* server API — a named fn per endpoint,
 *                         one private authed-fetch transport underneath
 *
 * The pane never writes to the email body except through secure_link.js; the
 * session_oid is presented as an access token on the Authorization: Bearer header
 * (RFC 6750) by js/utils/api.js.
 */

import { Events } from "../../js/utils/events.js";
import { getSessionId } from "../../js/utils/roaming_settings.js";
import { resetToConnect } from "../../js/utils/session.js";
import { signout } from "../../js/utils/api.js";
import {
  onPrimaryAction,
  addFiles,
  removeEntry,
  handleCancel,
  onFilesViewMounted,
  moveAttachmentsToLeapfile,
  dismissAttachmentPrompt,
} from "./files_actions.js";

let initialized = false;

export function initFiles() {
  if (initialized) return;
  initialized = true;

  // The Files view is HTMX-swapped into the pane; (re)build the queue on mount.
  document.body.addEventListener("htmx:afterSwap", (evt) => {
    if (isFilesViewMount(evt)) onFilesViewMounted();
  });

  // Picker change → queue + upload each file.
  Events.on("change", "oa-file-input", async (e, el) => {
    const picked = Array.from(el.files || []);
    el.value = "";
    await addFiles(picked);
  });

  Events.on("click", "oa-send", onPrimaryAction);
  Events.on("click", "oa-cancel", handleCancel);
  Events.on("click", "oa-signout", handleSignout);
  // The per-row remove control is rendered by files_view.js with data-oa-remove;
  // delegate here (like the other oa-* actions) so the view doesn't reach into the
  // action layer. The row's id lives on the enclosing <li data-id>.
  Events.on("click", "oa-remove", (e, el) => {
    const li = el.closest("li[data-id]");
    if (li) removeEntry(li.dataset.id);
  });

  // Large-attachment banner (rendered by files_view.js when the draft's own
  // attachments cross the threshold).
  Events.on("click", "oa-move-attachments", moveAttachmentsToLeapfile);
  Events.on("click", "oa-dismiss-attachments", dismissAttachmentPrompt);
}

// Sign out: end the session and unlink the Microsoft account server-side, then
// drop all pane state and return to Connect. Best-effort on the response — even
// if the POST fails we still tear down locally so the user is never stranded in a
// half-signed-out pane; the now-dead session would 401 on the next action anyway.
async function handleSignout() {
  if (getSessionId()) {
    try {
      await signout();
    } catch (err) {
      console.warn("[Files] signout request failed:", err);
    }
  }
  await resetToConnect();
}

/** True when this swap mounted the Files view (its root entered the DOM). */
function isFilesViewMount(evt) {
  const target = evt.detail && evt.detail.target;
  if (!target || typeof target.querySelector !== "function") return false;
  return !!target.querySelector("[data-oa-files-view]") || (target.matches && target.matches("[data-oa-files-view]"));
}
